// A shared board, read only, live. It loads into the same store the app uses,
// so it draws with the very same widgets, and listens on its own socket.

import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import type { Activity, AgentState, Board, CustomTheme, Entry, Item, ServerEvent, Stream, WidgetItem } from "../../shared/types";
import { kindOf } from "../../shared/boardKinds";
import { KINDS } from "../boards/kinds";
import { NotHere } from "../boards/Shell";
import { api } from "../lib/api";
import { glowItems, set, useStore } from "../state/store";
import { applyOps } from "../state/sync";
import { Canvas } from "../canvas/Canvas";
import { Backdrop } from "../themes/Backdrop";
import { resolveTheme, setMadeThemes, showsPhoto, THEME } from "../themes/themes";
import { ZoomControls } from "../chrome/Rail";
import { Mark } from "../chrome/Logo";
import { MobileBoard } from "./MobileBoard";
import { cx } from "../lib/util";
import { phoneSized } from "../phone/size";
import { PhoneChrome } from "../phone/PhoneChrome";
import { shareView } from "../../shared/sharePage";

// the link as a web page, when its owner chose that (S4): its own code, loaded only then
const WebPage = lazy(() => import("./WebPage"));

interface PublicSnap {
  board: Board;
  owner: { name: string; handle: string | null };
  items: Item[];
  /** a typed board's own pages, without who changed them */
  entries?: Entry[];
  streams: Stream[];
  agents: AgentState[];
  activity: Activity[];
  serverTime: number;
  /** the made theme the board wears, when it wears one, without who made it or the links its pictures came from */
  theme?: CustomTheme | null;
}

function load(snap: PublicSnap) {
  const items: Record<string, Item> = {};
  for (const i of snap.items) items[i.id] = i;
  const entries: Record<string, Entry> = {};
  for (const e of snap.entries ?? []) entries[e.id] = e;
  const streams: Record<string, Stream> = {};
  for (const s of snap.streams) streams[s.key] = s;
  const agents: Record<string, AgentState> = {};
  for (const a of snap.agents) agents[a.name] = a;
  if (snap.theme) setMadeThemes([snap.theme]);
  set({ boards: [snap.board], boardId: snap.board.id, items, entries, streams, agents, activity: snap.activity, themes: snap.theme ? { [snap.theme.id]: snap.theme } : {}, serverOffset: snap.serverTime - Date.now(), phase: "ready", keys: [] });
}

export function PublicBoard({ slug }: { slug: string }) {
  const [snap, setSnap] = useState<PublicSnap | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const board = useStore((s) => s.boards[0] ?? null);
  const mobile = useStore((s) => s.mobile);
  // the theme the board wears comes again when the owner switches to another or changes it
  useStore((s) => s.themes);
  const embed = useMemo(() => new URLSearchParams(location.search).has("embed"), []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let tries = 0;
    let stop = false;
    api<PublicSnap>(`/api/public/${slug}`, { token: null }).then(
      (s) => {
        load(s);
        setSnap(s);
        document.title = s.board.name;
        const connect = () => {
          if (stop) return;
          ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/public/${slug}/ws`);
          ws.onopen = () => {
            tries = 0;
            setLive(true);
          };
          ws.onmessage = (e) => {
            if (e.data === "pong") return;
            const ev = JSON.parse(e.data) as ServerEvent;
            if (ev.type === "ops") {
              applyOps(ev.ops);
              glowItems(ev.ops.filter((o) => o.op === "item.put").map((o) => (o as { item: Item }).item.id));
            } else if (ev.type === "stream") {
              set((st) => ({ streams: { ...st.streams, [ev.stream.key]: ev.stream } }));
              glowItems(
                Object.values(useStore.getState().items)
                  .filter((i) => i.kind === "widget" && i.stream === ev.stream.key)
                  .map((i) => i.id),
              );
            } else if (ev.type === "theme") {
              // kept beside the one before, which the board wears until its own change arrives
              const themes = { ...useStore.getState().themes, [ev.theme.id]: ev.theme };
              setMadeThemes(Object.values(themes));
              set({ themes });
            } else if (ev.type === "agent") set((st) => ({ agents: { ...st.agents, [ev.agent.name]: ev.agent } }));
            else if (ev.type === "activity") set((st) => ({ activity: [ev.activity, ...st.activity].slice(0, 100) }));
          };
          ws.onclose = (e) => {
            setLive(false);
            if (e.code === 4403) {
              setErr("This board is not shared any more.");
              return;
            }
            setTimeout(connect, Math.min(30_000, 1000 * 2 ** tries++));
          };
        };
        connect();
      },
      (e) => setErr((e as Error).message),
    );
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25_000);
    const onResize = () => set({ mobile: phoneSized() });
    window.addEventListener("resize", onResize);
    return () => {
      stop = true;
      clearInterval(ping);
      ws?.close();
      window.removeEventListener("resize", onResize);
    };
  }, [slug]);

  const theme = resolveTheme(board?.theme ?? { id: "meadow" });
  if (err)
    return (
      <div className="board-root public" data-mode="dark" style={theme.vars as React.CSSProperties}>
        <Backdrop def={THEME.meadow} motion dim={0.2} />
        <div className="public-gone island">
          <Mark />
          <h2>{err}</h2>
          <a className="btn btn-primary" href="/">
            Make your own board <ArrowRight size={14} />
          </a>
        </div>
      </div>
    );
  if (!snap || !board) return <div className="boot" />;
  // the owner's choice, live: switched in Share, the page a visitor has open follows
  if (shareView(board.share) === "page")
    return (
      <Suspense fallback={<div className="boot" />}>
        <WebPage board={board} owner={snap.owner.handle ?? (snap.owner.name && snap.owner.name !== "My workspace" ? snap.owner.name : null)} live={live} slug={slug} />
      </Suspense>
    );
  const widgets = Object.values(useStore.getState().items).filter((i): i is WidgetItem => i.kind === "widget");
  return (
    <div className={cx("board-root public", embed && "is-embed")} data-mode={theme.def.mode} data-pixel={theme.def.pixel ? "1" : undefined} data-photo={showsPhoto(theme.def, board.theme.backdrop) ? "1" : undefined} style={theme.vars as React.CSSProperties}>
      <Backdrop def={theme.def} motion={theme.motion} dim={theme.dim} custom={board.theme.backdrop ?? null} pick={board.theme.bg ?? 0} />
      {mobile ? <><MobileBoard readOnly itemsOverride={widgets} title={board.name} /><PhoneChrome /></> : kindOf(board) !== "canvas" ? <SharedKind board={board} /> : <Canvas readOnly />}
      {!mobile && (
        <div className="public-pill island">
          <Mark size={18} />
          <span className="public-name">
            {board.emoji ? `${board.emoji} ` : ""}
            {board.name}
          </span>
          {(snap.owner.handle || (snap.owner.name && snap.owner.name !== "My workspace")) && <span className="public-owner">by {snap.owner.handle ?? snap.owner.name}</span>}
          <span className={cx("public-live", live && "is-live")}>
            <i /> {live ? "Live" : "Connecting"}
          </span>
        </div>
      )}
      {!mobile && kindOf(board) === "canvas" && <ZoomControls simple />}
      {board.share.badge && (
        <a className="made-with island" href="https://contextbolt.com/board/?utm_source=shared_board&utm_medium=referral" target="_blank" rel="noopener">
          <Mark size={15} /> Made with Context Board <ArrowRight size={13} />
        </a>
      )}
    </div>
  );
}

/** A typed board shared by link: its kind's read-only view, in the canvas's place. */
function SharedKind({ board }: { board: Board }) {
  const Shared = KINDS[kindOf(board)]?.Shared;
  return (
    <div className={cx("shell is-public")} data-kind={kindOf(board)}>
      {Shared ? (
        <Suspense fallback={null}>
          <Shared board={board} />
        </Suspense>
      ) : (
        <NotHere board={board} />
      )}
    </div>
  );
}
